import {
    InfiniteData,
    QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
    useInfiniteQuery
} from '@tanstack/react-query';
import {
    Creative,
    SupportedCountry,
    complianceNameToCodeId,
    type ComplianceCodeName,
    createPollingGenerator
} from 'bigdatr-style';
import type {
    ComplianceReviewCreativeQuery,
    ComplianceCsvInput,
    CreativeItemV3,
    ComplianceCreativeListSort,
    ComplianceCreativeProgressStatus,
    ComplianceCreativeUpdateMutationInput,
    CompliancCodes
} from '~/graphql-react-query/graphql';
import {QueryStringFilters} from '~/feature/filter/types';
import useClientMainAuthorizedFetch from '../clientMainAuthorisedFetch';
import {ComplianceCreativeItem as ComplianceCreativeItemQuery} from '../creative/graphqlQueries';
import ensureDatesAreWithinTrialLimits from '~/creative/util/ensureDatesAreWithinTrialLimits';
import {applyComplianceExploreCreativeDateFloor} from './complianceExploreConstants';
import {useCountry} from '~/feature/country/CountryContext';
import {
    ComplianceCsv,
    ComplianceReviewCreativeQuery as ComplianceReviewCreativeQueryDocument,
    ComplianceUpdateCreative,
    ComplianceCreativeListV2,
    ComplianceCodeList
} from './graphqlQueries';
import {complianceShortlistQueryKeys} from './helpers';
import useEarliestSelectableCreativeDate from '~/affordance/dates/useEarliestSelectableDate';
import {ApiError} from 'bigdatr-style/src/error/errors';
import {DEFAULT_COMPLIANCE_PROGRESS_STATUS} from '~/feature/compliance-review/complianceProgressStatus';

type ComplianceShortlistCreative = NonNullable<
    NonNullable<ComplianceReviewCreativeQuery['compliance']>['creative']
>;

type ComplianceCreativeReviewMutationArgs = {
    complianceCode: ComplianceCodeName;
    creativeId: string;
    notes?: string[];
    progressStatus?: ComplianceCreativeProgressStatus;
};

export type ComplianceCreativeReview = {
    creativeId: string;
    complianceCodeId: string;
    progressStatus: ComplianceCreativeProgressStatus;
    notes: string[];
    history: NonNullable<ComplianceShortlistCreative['history']>;
    hasExistingRecord: boolean;
};

type ComplianceDataRow = NonNullable<CreativeItemV3['compliance']>[number];

function findComplianceForActiveCode(
    complianceFromCreative: CreativeItemV3['compliance'],
    activeComplianceCodeId: string
): ComplianceDataRow | undefined {
    return complianceFromCreative?.find(
        (row) => row?.compliance_code_id === activeComplianceCodeId
    );
}

function mapComplianceShortlistCreativeToReview(
    creative: ComplianceShortlistCreative
): ComplianceCreativeReview {
    return {
        creativeId: creative.creativeId,
        complianceCodeId: creative.complianceCodeId,
        progressStatus: creative.progressStatus,
        notes: creative.notes ?? [],
        history: creative.history ?? [],
        hasExistingRecord: true
    };
}

function setComplianceCreativeReviewQueryCache(
    queryClient: QueryClient,
    args: Pick<ComplianceCreativeReviewMutationArgs, 'complianceCode' | 'creativeId'>,
    review: ComplianceCreativeReview
) {
    const complianceCodeId = complianceNameToCodeId(args.complianceCode);
    queryClient.setQueryData(
        complianceShortlistQueryKeys.review(complianceCodeId, args.creativeId),
        review
    );
}

type CompliancePagedCreatives = {
    creatives: Creative[];
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    currentPage?: number;
};

type ComplianceInfinitePageV2 = {
    creatives: Creative[];
    paginationInfo: {
        nextPageCursor?: string[] | null;
        requestedCreatives: number;
        receivedCreatives: number;
    };
};

function patchCreativesProgressStatus(
    creatives: Creative[],
    creativeId: string,
    progressStatus: ComplianceCreativeProgressStatus
): Creative[] {
    return creatives.map((creative) =>
        creative.id === creativeId
            ? creative.withComplianceProgressStatus(progressStatus)
            : creative
    );
}

function patchComplianceExploreListCaches(
    queryClient: QueryClient,
    creativeId: string,
    progressStatus: ComplianceCreativeProgressStatus
) {
    queryClient.setQueriesData<CompliancePagedCreatives>(
        {queryKey: ['compliance-results']},
        (existing) => {
            if (!existing) return existing;
            return {
                ...existing,
                creatives: patchCreativesProgressStatus(
                    existing.creatives,
                    creativeId,
                    progressStatus
                )
            };
        }
    );

    queryClient.setQueriesData<InfiniteData<CompliancePagedCreatives>>(
        {queryKey: ['infinite-compliance-results']},
        (existing) => {
            if (!existing) return existing;
            return {
                ...existing,
                pages: existing.pages.map((page) => ({
                    ...page,
                    creatives: patchCreativesProgressStatus(
                        page.creatives,
                        creativeId,
                        progressStatus
                    )
                }))
            };
        }
    );

    queryClient.setQueriesData<InfiniteData<ComplianceInfinitePageV2>>(
        {queryKey: ['infinite-compliance-results-v2']},
        (existing) => {
            if (!existing) return existing;
            return {
                ...existing,
                pages: existing.pages.map((page) => ({
                    ...page,
                    creatives: patchCreativesProgressStatus(
                        page.creatives,
                        creativeId,
                        progressStatus
                    )
                }))
            };
        }
    );
}

function isComplianceCreativeNotFoundError(error: unknown): boolean {
    if (!(error instanceof ApiError)) return false;
    if (error.statusCode === 404) return true;
    if (error.name === 'NotFoundError') return true;
    return error.message === 'Compliance creative not found';
}

// The review API returns 404 when no record exists yet. Normalise that here so consumers
// always get a stable review shape with hasExistingRecord: false instead of handling errors.
function createDefaultComplianceCreativeReview(args: {
    complianceCodeId: string;
    creativeId: string;
}): ComplianceCreativeReview {
    return {
        creativeId: args.creativeId,
        complianceCodeId: args.complianceCodeId,
        progressStatus: DEFAULT_COMPLIANCE_PROGRESS_STATUS,
        notes: [],
        history: [],
        hasExistingRecord: false
    };
}

export function useInfiniteComplianceResultsV2(args: {
    complianceCodeName: ComplianceCodeName;
    filter?: QueryStringFilters;
    orderBy: ComplianceCreativeListSort;
    enabled?: boolean;
}) {
    const {orderBy, complianceCodeName, enabled = true} = args;
    const {isComplianceOnTrial} = useCountry();

    const earliestSelectableDate = useEarliestSelectableCreativeDate({type: 'compliance'});
    const request = useClientMainAuthorizedFetch();

    const {creativeDateRange, ...otherFilters} = args.filter ?? {};
    const dateWithinLimitations = applyComplianceExploreCreativeDateFloor(
        ensureDatesAreWithinTrialLimits({
            originalDate: creativeDateRange,
            isTrialing: isComplianceOnTrial,
            earliestSelectableDate
        })
    );

    const filters = {
        ...otherFilters,
        country: SupportedCountry.AU,
        dateRangeFilter: dateWithinLimitations,
        compliance_code_id: complianceNameToCodeId(complianceCodeName)
    };

    return useInfiniteQuery({
        enabled,
        queryKey: [
            'infinite-compliance-results-v2',
            complianceCodeName,
            otherFilters,
            filters,
            orderBy
        ],
        queryFn: async ({pageParam}) => {
            const response = await request(ComplianceCreativeListV2, {
                input: {
                    filters,
                    orderBy,
                    cursor: pageParam
                }
            });
            const rawResponse = response.compliance?.creativeListV2;
            if (!rawResponse)
                throw new Error('Compliance infinite creative list not returned from api');

            const {items, paginationInfo} = rawResponse;
            const activeComplianceCodeId = complianceNameToCodeId(complianceCodeName);
            const creatives = items.map((item) =>
                mapOnlyActiveCodeToCreative(item, activeComplianceCodeId)
            );

            return {
                creatives,
                paginationInfo
            };
        },
        initialPageParam: undefined as undefined | string[],
        getNextPageParam: (lastPage) => {
            if (lastPage.paginationInfo.mightHaveNextPage) {
                return lastPage.paginationInfo.nextPageCursor;
            }
            return null;
        }
    });
}

export function useComplianceCreative(args: {
    creativeId: string;
    complianceCodeName: ComplianceCodeName;
    enabled?: boolean;
}) {
    const {creativeId, complianceCodeName, enabled = true} = args;
    const request = useClientMainAuthorizedFetch();

    return useQuery({
        enabled,
        queryKey: ['compliance-creative', creativeId, complianceCodeName],
        queryFn: async () => {
            const response = await request(ComplianceCreativeItemQuery, {creativeId});
            const rawCreative = response.creativeV2?.publicCreativeItemV2;
            if (!rawCreative) throw new Error('Creative not returned from api');

            return mapOnlyActiveCodeToCreative(
                rawCreative,
                complianceNameToCodeId(complianceCodeName)
            );
        }
    });
}

export function useComplianceCodeList(args: {country: string; enabled?: boolean}) {
    const {country, enabled = true} = args;
    const request = useClientMainAuthorizedFetch();
    return useQuery({
        enabled,
        queryKey: ['compliance-codelist'],
        queryFn: async (): Promise<CompliancCodes[]> => {
            const response = await request(ComplianceCodeList);
            const complianceCodeList = response.compliance?.codeList;
            if (!complianceCodeList) {
                return [];
            }

            return complianceCodeList.items.filter((item) => item.country === country);
        }
    });
}

export function useComplianceCreativeReview(args: {
    complianceCode: ComplianceCodeName;
    creativeId: string;
    enabled?: boolean;
}) {
    const {complianceCode, creativeId, enabled = true} = args;
    const request = useClientMainAuthorizedFetch();
    const complianceCodeId = complianceNameToCodeId(complianceCode);

    return useQuery({
        // always refetch compliance information for a single creative once mounted to ensure people
        // see most recent review progress, history and notes
        staleTime: () => {
            return 0;
        },
        enabled,
        queryKey: complianceShortlistQueryKeys.review(complianceCodeId, creativeId),
        queryFn: async (): Promise<ComplianceCreativeReview> => {
            try {
                const response = await request(ComplianceReviewCreativeQueryDocument, {
                    complianceCodeId,
                    creativeId
                });
                const creative = response.compliance?.creative;
                if (!creative) {
                    return createDefaultComplianceCreativeReview({complianceCodeId, creativeId});
                }
                return mapComplianceShortlistCreativeToReview(creative);
            } catch (error) {
                if (isComplianceCreativeNotFoundError(error)) {
                    return createDefaultComplianceCreativeReview({complianceCodeId, creativeId});
                }
                throw error;
            }
        }
    });
}

export function useComplianceUpdateCreative() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: ComplianceCreativeReviewMutationArgs) => {
            const {complianceCode, creativeId, notes, progressStatus} = args;

            const input: ComplianceCreativeUpdateMutationInput = {
                complianceCodeId: complianceNameToCodeId(complianceCode),
                creativeId
            };

            if (notes !== undefined) {
                input.notes = notes;
            }
            if (progressStatus !== undefined) {
                input.progressStatus = progressStatus;
            }

            const response = await request(ComplianceUpdateCreative, {input});
            const updated = response.compliance?.updateComplianceCreative;
            // GraphQL mutation payloads are nullable even on success.
            if (!updated) {
                throw new Error('Compliance creative review update not returned from api');
            }

            return updated;
        },
        onSuccess: async (updated, args) => {
            setComplianceCreativeReviewQueryCache(
                queryClient,
                args,
                mapComplianceShortlistCreativeToReview(updated)
            );
            patchComplianceExploreListCaches(queryClient, args.creativeId, updated.progressStatus);
        }
    });
}

export function useComplianceCsv() {
    const request = useClientMainAuthorizedFetch();
    const {isComplianceOnTrial} = useCountry();

    const earliestSelectableDate = useEarliestSelectableCreativeDate({type: 'compliance'});

    return useMutation({
        mutationFn: async (input: ComplianceCsvInput) => {
            const pollingData = createPollingGenerator({
                createRequest: async () => {
                    const {dateRangeFilter, ...otherFilters} = input.filters;

                    const sanitizedDate = applyComplianceExploreCreativeDateFloor(
                        ensureDatesAreWithinTrialLimits({
                            originalDate: dateRangeFilter ?? undefined,
                            isTrialing: isComplianceOnTrial,
                            earliestSelectableDate
                        })
                    );

                    const response = await request(ComplianceCsv, {
                        input: {
                            ...input,
                            filters: {
                                ...otherFilters,
                                dateRangeFilter: sanitizedDate
                            },
                            includeReviewProgress: true
                        }
                    });
                    return response.compliance?.csv;
                },
                pollRequest: async (response) => {
                    const {csv, errorJson} = response || {};
                    if (!csv || !errorJson) {
                        throw new Error('CSV or error JSON signed URLsnot returned from API');
                    }

                    // make a request to the signed URLs and return their statuses
                    const [csvResponse, errorJsonResponse] = await Promise.all([
                        fetch(csv),
                        fetch(errorJson)
                    ]);

                    const hasError = errorJsonResponse.status === 200;
                    const isCsvFileReady = csvResponse.status === 200;
                    return {
                        csv,
                        errorJson,
                        isCsvFileReady,
                        downloadStatus: csvResponse.status,
                        errorStatus: errorJsonResponse.status,
                        hasError
                    };
                },
                pollWhile: ({isCsvFileReady, hasError}) => {
                    // stop polling if an error file shows up, or if the csv file shows up
                    return !hasError && !isCsvFileReady;
                },
                interval: 2000
            });

            let lastPayload: Awaited<ReturnType<(typeof pollingData)['next']>>['value'] | null =
                null;
            for await (const payload of pollingData) {
                lastPayload = payload;
                // if an error file exists at the given URL, fetch the JSON with the error message
                // and expose it to the user by throwing it
                if (payload.hasError) {
                    const errorResponse = await fetch(payload.errorJson);
                    const json = await errorResponse.json();
                    const error = new Error(json.error);
                    error.name = json.name;
                    error.stack = json.stack;
                    throw error;
                }
            }
            return lastPayload;
        }
    });
}

type ComplianceCreativeListRow = CreativeItemV3 & {
    progressStatus?: ComplianceCreativeProgressStatus | null;
};

function mapOnlyActiveCodeToCreative(
    item: ComplianceCreativeListRow,
    activeComplianceCodeId: string
): Creative {
    const {compliance, progressStatus, ...rest} = item;
    const row = findComplianceForActiveCode(compliance, activeComplianceCodeId);
    return new Creative({
        ...rest,
        complianceRiskRating: row?.compliance_risk_rating ?? null,
        complianceReason: row?.compliance_reason ?? null,
        complianceProgressStatus: progressStatus
    });
}
